export const ANONYMOUS_STORAGE_SCOPE = "anon";
export const ACCOUNT_STORAGE_BOOTSTRAP_MARKER_KEY = "tb_public_web_account_bootstrap_v1";
export const AUTHENTICATED_LOCAL_DATA_BOOTSTRAP_MAX_BYTES = 8 * 1024 * 1024;

const ACCOUNT_SCOPE_RE = /^account_[a-zA-Z0-9._-]{1,96}$/;
const BOOTSTRAP_MARKER_VERSION = 2;
const STAGED_VALUE_HASH_VERSION = 1;
const STAGED_VALUE_HASH_RE = /^\d+:[0-9a-f]{16}$/;
const BOOTSTRAP_PUSH_OPTION_KEYS = [
  "includePreferences",
  "includeTheme",
  "includeTasks",
  "includeSavedArticles",
  "includeWatchlist",
  "includeStreak",
];
const BOOTSTRAP_PENDING_PUSH_KEY_PREFIX = "tb_public_web_account_bootstrap_pending_push_v1";
let pendingPushTokenSequence = 0;

export function accountStorageScopeForStableId(value) {
  const stableId = String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 96);
  return stableId ? `account_${stableId}` : null;
}

function scopedSuffix(scope) {
  return `::${scope}`;
}

function bootstrapMarkerKey(scope) {
  return `${ACCOUNT_STORAGE_BOOTSTRAP_MARKER_KEY}${scopedSuffix(scope)}`;
}

function bootstrapPendingPushKey(scope, option) {
  return `${BOOTSTRAP_PENDING_PUSH_KEY_PREFIX}:${option}${scopedSuffix(scope)}`;
}

function newPendingPushToken() {
  pendingPushTokenSequence = (pendingPushTokenSequence + 1) % Number.MAX_SAFE_INTEGER;
  return [
    Date.now().toString(36),
    pendingPushTokenSequence.toString(36),
    Math.random().toString(36).slice(2),
  ].join(".");
}

function readPendingPushTokens(storage, scope) {
  const tokens = {};
  for (const option of BOOTSTRAP_PUSH_OPTION_KEYS) {
    const value = storage.getItem(bootstrapPendingPushKey(scope, option));
    if (value != null) tokens[option] = value;
  }
  return tokens;
}

function stagedValueHash(value) {
  const input = String(value);
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    first = Math.imul(first ^ (code & 0xff), 0x01000193);
    first = Math.imul(first ^ (code >>> 8), 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
    second ^= second >>> 13;
  }
  const firstHex = (first >>> 0).toString(16).padStart(8, "0");
  const secondHex = (second >>> 0).toString(16).padStart(8, "0");
  return `${input.length}:${firstHex}${secondHex}`;
}

function utf8ByteLength(value) {
  const input = String(value);
  if (typeof TextEncoder === "function") {
    return new TextEncoder().encode(input).byteLength;
  }
  let bytes = 0;
  for (let index = 0; index < input.length; index += 1) {
    const codePoint = input.codePointAt(index);
    if (codePoint <= 0x7f) bytes += 1;
    else if (codePoint <= 0x7ff) bytes += 2;
    else if (codePoint <= 0xffff) bytes += 3;
    else {
      bytes += 4;
      index += 1;
    }
  }
  return bytes;
}

function normalizedStagedValueHashes(marker, baseKeys) {
  if (marker?.version === 1) return {};
  const raw = marker?.stagedValueHashes;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const allowedBaseKeys = new Set(baseKeys);
  const normalized = Object.create(null);
  for (const [baseKey, value] of Object.entries(raw)) {
    if (
      !allowedBaseKeys.has(baseKey) ||
      !value ||
      typeof value !== "object" ||
      Array.isArray(value) ||
      value.version !== STAGED_VALUE_HASH_VERSION ||
      typeof value.hash !== "string" ||
      !STAGED_VALUE_HASH_RE.test(value.hash) ||
      (Object.prototype.hasOwnProperty.call(value, "pendingHash") &&
        (typeof value.pendingHash !== "string" || !STAGED_VALUE_HASH_RE.test(value.pendingHash)))
    ) {
      return null;
    }
    normalized[baseKey] = {
      version: STAGED_VALUE_HASH_VERSION,
      hash: value.hash,
      ...(value.pendingHash ? { pendingHash: value.pendingHash } : {}),
    };
  }
  return normalized;
}

function normalizedCloudBootstrapRequest(value) {
  if (value == null) return null;
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    value.version !== 1 ||
    typeof value.idempotencyKey !== "string" ||
    value.idempotencyKey.length < 16 ||
    value.idempotencyKey.length > 128 ||
    typeof value.bodyJson !== "string" ||
    value.bodyJson.length < 2 ||
    utf8ByteLength(value.bodyJson) > AUTHENTICATED_LOCAL_DATA_BOOTSTRAP_MAX_BYTES
  ) {
    return undefined;
  }
  try {
    const body = JSON.parse(value.bodyJson);
    if (
      !body ||
      typeof body !== "object" ||
      Array.isArray(body) ||
      body.completeLocalDataBootstrap !== true ||
      typeof body.deviceId !== "string" ||
      !body.deviceId.trim()
    ) {
      return undefined;
    }
  } catch {
    return undefined;
  }
  return {
    version: 1,
    idempotencyKey: value.idempotencyKey,
    bodyJson: value.bodyJson,
  };
}

function normalizedPendingPushOptions(value) {
  if (value == null) return {};
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const normalized = {};
  for (const [key, enabled] of Object.entries(value)) {
    if (!BOOTSTRAP_PUSH_OPTION_KEYS.includes(key) || enabled !== true) return null;
    normalized[key] = true;
  }
  return normalized;
}

function normalizedPendingPushReceipt(value) {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    value.version !== 1 ||
    !Number.isSafeInteger(value.markerGeneration) ||
    value.markerGeneration < 0 ||
    !value.tokens ||
    typeof value.tokens !== "object" ||
    Array.isArray(value.tokens)
  ) {
    return null;
  }
  const tokens = {};
  for (const [option, token] of Object.entries(value.tokens)) {
    if (!BOOTSTRAP_PUSH_OPTION_KEYS.includes(option) || typeof token !== "string" || !token) {
      return null;
    }
    tokens[option] = token;
  }
  return {
    version: 1,
    markerGeneration: value.markerGeneration,
    tokens,
  };
}

function readBootstrapMarker(storage, scope) {
  try {
    const raw = storage.getItem(bootstrapMarkerKey(scope));
    if (raw == null) return null;
    const marker = JSON.parse(raw);
    const baseKeys = Array.isArray(marker?.baseKeys)
      ? [...new Set(marker.baseKeys.filter((key) => typeof key === "string" && key.length > 0))]
      : [];
    if (
      (marker?.version !== 1 && marker?.version !== BOOTSTRAP_MARKER_VERSION) ||
      marker?.sourceScope !== ANONYMOUS_STORAGE_SCOPE ||
      (marker?.phase !== "staging" &&
        marker?.phase !== "bootstrap_pending" &&
        marker?.phase !== "server_completed") ||
      (Object.prototype.hasOwnProperty.call(marker, "preserveExistingTarget") &&
        marker.preserveExistingTarget !== true) ||
      baseKeys.length !== marker?.entryCount
    ) {
      return null;
    }
    const stagedValueHashes = normalizedStagedValueHashes(marker, baseKeys);
    const cloudBootstrapRequest = normalizedCloudBootstrapRequest(marker.cloudBootstrapRequest);
    const pendingPushOptions = normalizedPendingPushOptions(marker.pendingPushOptions);
    const pendingPushGeneration =
      Number.isSafeInteger(marker.pendingPushGeneration) && marker.pendingPushGeneration >= 0
        ? marker.pendingPushGeneration
        : Object.keys(pendingPushOptions || {}).length > 0
          ? 1
          : 0;
    if (
      stagedValueHashes == null ||
      cloudBootstrapRequest === undefined ||
      pendingPushOptions == null
    ) {
      return null;
    }
    return {
      ...marker,
      baseKeys,
      stagedValueHashes,
      ...(cloudBootstrapRequest ? { cloudBootstrapRequest } : {}),
      pendingPushOptions,
      pendingPushGeneration,
    };
  } catch {
    return null;
  }
}

function bootstrapMarkerPayload(
  phase,
  baseKeys,
  stagedValueHashes,
  preserveExistingTarget,
  cloudBootstrapRequest = null,
  pendingPushOptions = {},
  pendingPushGeneration = 0,
) {
  return {
    version: BOOTSTRAP_MARKER_VERSION,
    phase,
    sourceScope: ANONYMOUS_STORAGE_SCOPE,
    entryCount: baseKeys.length,
    baseKeys,
    stagedValueHashes,
    ...(preserveExistingTarget ? { preserveExistingTarget: true } : {}),
    ...(cloudBootstrapRequest ? { cloudBootstrapRequest } : {}),
    ...(Object.keys(pendingPushOptions).length > 0 ? { pendingPushOptions } : {}),
    ...(pendingPushGeneration > 0 ? { pendingPushGeneration } : {}),
  };
}

function entriesForScope(storage, scope) {
  const suffix = scopedSuffix(scope);
  const entries = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !key.endsWith(suffix)) continue;
    const value = storage.getItem(key);
    if (value == null) continue;
    entries.push({ key, value });
  }
  entries.sort((left, right) => left.key.localeCompare(right.key));
  return entries;
}

/**
 * Removes only rows owned by one authenticated account scope. Anonymous rows,
 * install-level browser data, and rows for every other account are retained.
 *
 * The key list is captured before deletion so mutating Storage does not skip
 * entries. Browser storage can be unavailable or partially fail; deletion is
 * therefore best-effort and intentionally never throws after the server has
 * already permanently deleted the account.
 */
export function purgeAccountStorageScope(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return 0;
  let keys;
  try {
    keys = entriesForScope(storage, scope).map(({ key }) => key);
  } catch {
    return 0;
  }
  let removed = 0;
  for (const key of keys) {
    try {
      storage.removeItem(key);
      removed += 1;
    } catch {
      // Continue clearing the rest of this exact account scope.
    }
  }
  return removed;
}

function inactiveTransaction(outcome) {
  return Object.freeze({
    outcome,
    commit: () => outcome,
    rollback: () => false,
  });
}

/**
 * Captures read-only intent while the auth mutation still belongs to the
 * anonymous browser scope. No target writes happen until the server confirms
 * that this request created an account.
 */
export function captureAnonymousAccountStorageMigration(storage, sourceScope) {
  if (!storage || sourceScope !== ANONYMOUS_STORAGE_SCOPE) return null;
  const capturedEntries = entriesForScope(storage, sourceScope);
  return Object.freeze({
    sourceScope,
    capturedEntryCount: capturedEntries.length,
  });
}

/**
 * Stages an anonymous -> new-account copy as one rollback-capable operation.
 * The anonymous source is deliberately retained: an auth/storage failure must
 * never destroy the user's pre-signup browser data.
 */
export function stageAnonymousAccountStorageMigration(storage, intent, targetScope, options = {}) {
  if (!storage || !intent) return inactiveTransaction("not_anonymous");
  if (
    intent.sourceScope !== ANONYMOUS_STORAGE_SCOPE ||
    !ACCOUNT_SCOPE_RE.test(String(targetScope || ""))
  ) {
    return inactiveTransaction("invalid_target");
  }

  const sourceSuffix = scopedSuffix(ANONYMOUS_STORAGE_SCOPE);
  const targetSuffix = scopedSuffix(targetScope);
  const sourceEntries = entriesForScope(storage, ANONYMOUS_STORAGE_SCOPE).filter(
    ({ key }) => key !== bootstrapMarkerKey(ANONYMOUS_STORAGE_SCOPE),
  );

  const markerKey = bootstrapMarkerKey(targetScope);
  const existingMarker = readBootstrapMarker(storage, targetScope);
  const targetEntries = entriesForScope(storage, targetScope);
  const preserveExistingTarget =
    options?.preserveExistingTarget === true || existingMarker?.preserveExistingTarget === true;
  if (targetEntries.length > 0) {
    // A valid staging manifest makes a process-interrupted copy resumable.
    // Without it, or if unrelated target rows exist, fail closed rather than
    // overlaying a previously signed-in account.
    if (!existingMarker && !preserveExistingTarget) {
      return inactiveTransaction("target_not_empty");
    }
    const allowedTargetKeys = existingMarker
      ? new Set([
          markerKey,
          ...existingMarker.baseKeys.map((baseKey) => `${baseKey}${targetSuffix}`),
        ])
      : null;
    if (
      !preserveExistingTarget &&
      allowedTargetKeys &&
      targetEntries.some(({ key }) => !allowedTargetKeys.has(key))
    ) {
      return inactiveTransaction("target_not_empty");
    }
    if (existingMarker?.phase === "server_completed") {
      return inactiveTransaction("server_completed");
    }
    if (existingMarker?.phase === "bootstrap_pending") {
      const targetKeys = new Set(targetEntries.map(({ key }) => key));
      if (existingMarker.baseKeys.some((baseKey) => !targetKeys.has(`${baseKey}${targetSuffix}`))) {
        return inactiveTransaction("incomplete_source");
      }
      return inactiveTransaction("bootstrap_pending");
    }
    const sourceBaseKeys = new Set(
      sourceEntries.map(({ key }) => key.slice(0, -sourceSuffix.length)),
    );
    const targetKeys = new Set(targetEntries.map(({ key }) => key));
    const missingRecoverySource = (existingMarker?.baseKeys || []).some(
      (baseKey) => !sourceBaseKeys.has(baseKey) && !targetKeys.has(`${baseKey}${targetSuffix}`),
    );
    if (missingRecoverySource) {
      return inactiveTransaction("incomplete_source");
    }
  }

  const baseKeys = [
    ...new Set([
      ...(existingMarker?.baseKeys || []),
      ...(preserveExistingTarget
        ? targetEntries
            .filter(({ key }) => key !== markerKey)
            .map(({ key }) => key.slice(0, -targetSuffix.length))
        : []),
      ...sourceEntries.map(({ key }) => key.slice(0, -sourceSuffix.length)),
    ]),
  ].sort();
  const stagedValueHashes = Object.assign(
    Object.create(null),
    existingMarker?.stagedValueHashes || {},
  );
  const sourceEntriesToWrite = [];
  for (const entry of sourceEntries) {
    const baseKey = entry.key.slice(0, -sourceSuffix.length);
    const targetKey = `${baseKey}${targetSuffix}`;
    const targetValue = storage.getItem(targetKey);
    const sourceHash = stagedValueHash(entry.value);
    const priorHash = stagedValueHashes[baseKey];
    const targetHash = targetValue == null ? null : stagedValueHash(targetValue);
    const targetStillOwnedByStaging =
      existingMarker?.phase === "staging" &&
      priorHash &&
      (targetHash === priorHash.hash || targetHash === priorHash.pendingHash);

    if (targetValue == null) {
      stagedValueHashes[baseKey] = {
        version: STAGED_VALUE_HASH_VERSION,
        hash: sourceHash,
      };
      sourceEntriesToWrite.push({ ...entry, baseKey, targetKey, expectedTargetValue: null });
    } else if (targetStillOwnedByStaging) {
      if (targetValue !== entry.value) {
        stagedValueHashes[baseKey] = {
          version: STAGED_VALUE_HASH_VERSION,
          hash: targetHash,
          pendingHash: sourceHash,
        };
        sourceEntriesToWrite.push({
          ...entry,
          baseKey,
          targetKey,
          expectedTargetValue: targetValue,
        });
      } else {
        stagedValueHashes[baseKey] = {
          version: STAGED_VALUE_HASH_VERSION,
          hash: sourceHash,
        };
      }
    } else {
      // No manifest hash owns this target value, or the value changed after
      // staging. Treat it as signed-in account data and never overlay it.
      delete stagedValueHashes[baseKey];
    }
  }
  const previousValues = new Map();
  const keysToTouch = [markerKey, ...sourceEntriesToWrite.map(({ targetKey }) => targetKey)];
  for (const key of keysToTouch) previousValues.set(key, storage.getItem(key));
  const writtenTargetValues = new Map();
  const abandonedTargetKeys = new Set();

  let active = true;
  const rollbackWrites = () => {
    if (!active) return false;
    for (let index = keysToTouch.length - 1; index >= 0; index -= 1) {
      const key = keysToTouch[index];
      try {
        if (
          key !== markerKey &&
          (abandonedTargetKeys.has(key) ||
            (writtenTargetValues.has(key) && storage.getItem(key) !== writtenTargetValues.get(key)))
        ) {
          // Another tab edited the signed-in target after our write. That edit
          // is newer than this rollback and must remain authoritative.
          continue;
        }
        const previous = previousValues.get(key);
        if (previous == null) storage.removeItem(key);
        else storage.setItem(key, previous);
      } catch {
        // The source scope remains intact even if storage itself has become
        // unavailable, so retrying sign-up cannot lose the original data.
      }
    }
    active = false;
    return true;
  };

  try {
    // Manifest first: a browser/process crash between individual localStorage
    // writes leaves an explicitly recoverable target, never an ambiguous
    // partial account scope.
    storage.setItem(
      markerKey,
      JSON.stringify({
        ...bootstrapMarkerPayload(
          "staging",
          baseKeys,
          stagedValueHashes,
          preserveExistingTarget,
          existingMarker?.cloudBootstrapRequest,
          existingMarker?.pendingPushOptions,
          existingMarker?.pendingPushGeneration,
        ),
      }),
    );
    for (const entry of sourceEntriesToWrite) {
      if (storage.getItem(entry.targetKey) !== entry.expectedTargetValue) {
        // A signed-in edit won the race after the staging manifest was
        // written. Relinquish ownership instead of replacing that edit.
        delete stagedValueHashes[entry.baseKey];
        abandonedTargetKeys.add(entry.targetKey);
        continue;
      }
      storage.setItem(entry.targetKey, entry.value);
      writtenTargetValues.set(entry.targetKey, entry.value);
      if (storage.getItem(entry.targetKey) === entry.value) {
        stagedValueHashes[entry.baseKey] = {
          version: STAGED_VALUE_HASH_VERSION,
          hash: stagedValueHash(entry.value),
        };
      } else {
        delete stagedValueHashes[entry.baseKey];
      }
    }
    storage.setItem(
      markerKey,
      JSON.stringify(
        bootstrapMarkerPayload(
          "bootstrap_pending",
          baseKeys,
          stagedValueHashes,
          preserveExistingTarget,
          existingMarker?.cloudBootstrapRequest,
          existingMarker?.pendingPushOptions,
          existingMarker?.pendingPushGeneration,
        ),
      ),
    );
  } catch (error) {
    rollbackWrites();
    throw error;
  }

  return Object.freeze({
    outcome: "staged",
    commit() {
      if (!active) return "rolled_back";
      // Auth success makes the staged account scope eligible for use, but it
      // does not yet prove that the account bootstrap or even the subsequent
      // session validation will complete. Keep every anonymous source row as
      // a durable fallback until `completeAccountStorageBootstrap` observes
      // the explicit server-completed state and removes exact matching copies.
      active = false;
      return "committed";
    },
    rollback: rollbackWrites,
  });
}

export function hasPendingAccountStorageBootstrap(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  const phase = readBootstrapMarker(storage, scope)?.phase;
  return phase === "bootstrap_pending" || phase === "server_completed";
}

export function hasServerCompletedAccountStorageBootstrap(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  return readBootstrapMarker(storage, scope)?.phase === "server_completed";
}

export function hasRecoverableAnonymousAccountStorageMigration(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  const phase = readBootstrapMarker(storage, scope)?.phase;
  return phase === "staging" || phase === "bootstrap_pending" || phase === "server_completed";
}

export function readAccountStorageBootstrapRequest(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return null;
  const marker = readBootstrapMarker(storage, scope);
  if (marker?.phase !== "bootstrap_pending" && marker?.phase !== "server_completed") {
    return null;
  }
  return marker.cloudBootstrapRequest || null;
}

export function persistAccountStorageBootstrapRequest(storage, scope, request) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return null;
  const normalizedRequest = normalizedCloudBootstrapRequest(request);
  if (!normalizedRequest) return null;
  try {
    const markerKey = bootstrapMarkerKey(scope);
    const marker = readBootstrapMarker(storage, scope);
    if (!marker || marker.phase !== "bootstrap_pending") return null;
    if (marker.cloudBootstrapRequest) {
      return marker.cloudBootstrapRequest.idempotencyKey === normalizedRequest.idempotencyKey &&
        marker.cloudBootstrapRequest.bodyJson === normalizedRequest.bodyJson
        ? marker.cloudBootstrapRequest
        : null;
    }
    storage.setItem(
      markerKey,
      JSON.stringify({
        ...marker,
        cloudBootstrapRequest: normalizedRequest,
      }),
    );
    const persisted = readBootstrapMarker(storage, scope)?.cloudBootstrapRequest;
    return persisted?.idempotencyKey === normalizedRequest.idempotencyKey &&
      persisted.bodyJson === normalizedRequest.bodyJson
      ? persisted
      : null;
  } catch {
    return null;
  }
}

export function markAccountStorageBootstrapPushPending(storage, scope, options) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  const normalizedOptions = normalizedPendingPushOptions(options);
  if (!normalizedOptions) return false;
  try {
    const marker = readBootstrapMarker(storage, scope);
    if (!marker || (marker.phase !== "bootstrap_pending" && marker.phase !== "server_completed")) {
      return false;
    }
    const writtenTokens = {};
    for (const key of BOOTSTRAP_PUSH_OPTION_KEYS) {
      if (normalizedOptions[key] !== true) continue;
      const token = newPendingPushToken();
      const pendingKey = bootstrapPendingPushKey(scope, key);
      storage.setItem(pendingKey, token);
      if (storage.getItem(pendingKey) !== token) return false;
      writtenTokens[key] = token;
    }
    const currentMarker = readBootstrapMarker(storage, scope);
    if (
      !currentMarker ||
      (currentMarker.phase !== "bootstrap_pending" && currentMarker.phase !== "server_completed")
    ) {
      for (const [key, token] of Object.entries(writtenTokens)) {
        const pendingKey = bootstrapPendingPushKey(scope, key);
        if (storage.getItem(pendingKey) === token) storage.removeItem(pendingKey);
      }
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function readAccountStorageBootstrapPendingPushOptions(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return {};
  try {
    const marker = readBootstrapMarker(storage, scope);
    if (marker?.phase !== "bootstrap_pending" && marker?.phase !== "server_completed") {
      return {};
    }
    const options = { ...marker.pendingPushOptions };
    for (const key of Object.keys(readPendingPushTokens(storage, scope))) {
      options[key] = true;
    }
    return options;
  } catch {
    return {};
  }
}

export function readAccountStorageBootstrapPendingPushState(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) {
    return {
      generation: { version: 1, markerGeneration: 0, tokens: {} },
      options: {},
    };
  }
  try {
    const marker = readBootstrapMarker(storage, scope);
    if (marker?.phase !== "server_completed") {
      return {
        generation: { version: 1, markerGeneration: 0, tokens: {} },
        options: {},
      };
    }
    const tokens = readPendingPushTokens(storage, scope);
    const options = { ...marker.pendingPushOptions };
    for (const key of Object.keys(tokens)) options[key] = true;
    return {
      generation: {
        version: 1,
        markerGeneration: marker.pendingPushGeneration,
        tokens,
      },
      options,
    };
  } catch {
    return {
      generation: { version: 1, markerGeneration: 0, tokens: {} },
      options: {},
    };
  }
}

export function acknowledgeAccountStorageBootstrapPendingPush(storage, scope, generation) {
  const receipt = normalizedPendingPushReceipt(generation);
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || "")) || !receipt) {
    return false;
  }
  try {
    const markerKey = bootstrapMarkerKey(scope);
    const marker = readBootstrapMarker(storage, scope);
    if (
      marker?.phase !== "server_completed" ||
      marker.pendingPushGeneration !== receipt.markerGeneration
    ) {
      return false;
    }
    storage.setItem(markerKey, JSON.stringify({ ...marker, pendingPushOptions: {} }));
    let acknowledged = true;
    for (const [key, token] of Object.entries(receipt.tokens)) {
      const pendingKey = bootstrapPendingPushKey(scope, key);
      const current = storage.getItem(pendingKey);
      if (current === token) {
        storage.removeItem(pendingKey);
      } else if (current != null) {
        acknowledged = false;
      }
    }
    return acknowledged;
  } catch {
    return false;
  }
}

export function markAccountStorageBootstrapServerCompleted(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  try {
    const markerKey = bootstrapMarkerKey(scope);
    const marker = readBootstrapMarker(storage, scope);
    if (!marker) return false;
    if (marker.phase === "server_completed") return true;
    if (marker.phase !== "bootstrap_pending" || !marker.cloudBootstrapRequest) return false;
    storage.setItem(markerKey, JSON.stringify({ ...marker, phase: "server_completed" }));
    return readBootstrapMarker(storage, scope)?.phase === "server_completed";
  } catch {
    return false;
  }
}

/**
 * Restores the push-before-pull marker when the server proves that this exact
 * account/device still owes its first local-data bootstrap. Existing account
 * rows win and anonymous rows fill only missing keys, so a delayed recovery
 * can never replace edits already made while signed in.
 */
export function ensurePendingAccountStorageBootstrap(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  if (hasPendingAccountStorageBootstrap(storage, scope)) return true;

  const intent = captureAnonymousAccountStorageMigration(storage, ANONYMOUS_STORAGE_SCOPE);
  const transaction = stageAnonymousAccountStorageMigration(storage, intent, scope, {
    preserveExistingTarget: true,
  });
  if (transaction.outcome === "staged") {
    transaction.commit();
    return hasPendingAccountStorageBootstrap(storage, scope);
  }
  if (transaction.outcome === "bootstrap_pending") return true;
  return false;
}

export function completeAccountStorageBootstrap(storage, scope) {
  if (!storage || !ACCOUNT_SCOPE_RE.test(String(scope || ""))) return false;
  try {
    const markerKey = bootstrapMarkerKey(scope);
    const marker = readBootstrapMarker(storage, scope);
    if (!marker) return storage.getItem(markerKey) == null;
    if (marker.phase !== "server_completed") return false;
    if (
      Object.keys(marker.pendingPushOptions).length > 0 ||
      Object.keys(readPendingPushTokens(storage, scope)).length > 0
    ) {
      return false;
    }
    for (const baseKey of marker.baseKeys) {
      const sourceKey = `${baseKey}${scopedSuffix(ANONYMOUS_STORAGE_SCOPE)}`;
      const targetKey = `${baseKey}${scopedSuffix(scope)}`;
      const sourceValue = storage.getItem(sourceKey);
      const targetValue = storage.getItem(targetKey);
      // Recovery for a process death after staging finished but before the
      // in-memory transaction committed its source cleanup.
      if (sourceValue != null && sourceValue === targetValue) {
        storage.removeItem(sourceKey);
      }
    }
    storage.removeItem(markerKey);
    return true;
  } catch {
    return false;
  }
}
